import { getMsgDeskRuntime } from '@/msgdeskRuntime/runtime/MsgDeskRuntime'
import type { LegacyParsedElementNode, LegacyParsedNode } from '@/msgdeskRuntime/legacyBridge/LegacyHtmlAstTypes'
import { defaultSubUiState } from './types'
import type {
  EditorAttachmentState,
  EditorFormInputState,
  LegacyDataState,
  LegacySectionInput,
  MainAttachmentState,
  MoveCheckedOptions,
  MsgAccessGrant,
  MsgAccessState,
  MsgObjEditorState,
  MsgObjMainState,
  MsgPageSettingsState,
  ParentLink,
  ParentsMenuState,
  SectionPagingSummary,
  SubRenderState,
  SubsElementNodeData,
  SubsNodeData,
  SubsSection,
  SubsStoreState,
  SubVisualVariant,
  TemplateDataState,
} from './types'
import {
  attachmentMediaKind,
  attachmentMimeForAttachment,
} from '@/contracts/mediaAttachments'

function currentLegacyPagingMode(): string {
  const pagingObj = getMsgDeskRuntime().facade.pagingObj();
  try {
    const mode = pagingObj?.paging_mode?.();
    return typeof mode === 'string' && mode ? mode : 'msg';
  } catch {
    return 'msg';
  }
}

function firstDefined<T>(...values: T[]): T | undefined {
  for (const value of values) {
    if (value !== undefined && value !== null) return value;
  }
  return undefined;
}

function firstString(...values: unknown[]): string | undefined {
  for (const value of values) {
    if (typeof value === 'string') return value;
    if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  }
  return undefined;
}

function firstNonEmptyString(...values: unknown[]): string | undefined {
  for (const value of values) {
    const text = firstString(value);
    if (text && text.trim() !== '') return text;
  }
  return undefined;
}

function firstNumber(...values: unknown[]): number | undefined {
  for (const value of values) {
    if (typeof value === 'number' && Number.isFinite(value)) return value;
    if (typeof value === 'string' && value.trim() !== '') {
      const parsed = Number.parseInt(value, 10);
      if (!Number.isNaN(parsed)) return parsed;
    }
  }
  return undefined;
}

function boolFromLegacy(value: unknown): boolean | undefined {
  if (typeof value === 'boolean') return value;
  if (typeof value === 'number') return value !== 0;
  if (typeof value === 'string') {
    const normalized = value.trim().toLowerCase();
    if (['1', 'true', 'yes', 'on'].includes(normalized)) return true;
    if (['0', 'false', 'no', 'off', ''].includes(normalized)) return false;
  }
  return undefined;
}

function readRuntimeMainMsgData(): any | undefined {
  try {
    // Stage 2b: prefer the typed model transport (facade.model) — the
    // extracted data model is the single source; facade.msgData remains the
    // untyped fallback for the frozen JS runtime (no model view there).
    const facade = getMsgDeskRuntime().facade;
    return facade.model?.data.msg_data(0) ?? facade.msgData(0);
  } catch {
    return undefined;
  }
}

function readRuntimeSubMsgData(elId: number): any | undefined {
  try {
    // Guard: if legacy has removed this sub from its element map (e.g. after a move/delete),
    // don't call msg_data — it fires a modal alert for a missing element.
    const facade = getMsgDeskRuntime().facade;
    if (!facade.hasRuntimeSubElement(elId)) return undefined
    return facade.model?.data.msg_data(elId) ?? facade.msgData(elId);
  } catch {
    return undefined;
  }
}

function readRuntimeMsgInputs(elId = 0): Record<string, unknown> | undefined {
  try {
    return getMsgDeskRuntime().facade.msgInputs(elId);
  } catch {
    return undefined;
  }
}

function cleanPreviewText(value: string | null | undefined): string | undefined {
  const text = String(value ?? '').replace(/\s+/g, ' ').trim();
  return text || undefined;
}

function firstElementText(root: Element, selectors: string[]): string | undefined {
  for (const selector of selectors) {
    const el = root.querySelector(selector);
    const text = cleanPreviewText(el?.textContent);
    if (text) return text;
  }
  return undefined;
}

function firstElementHtml(root: Element, selectors: string[]): string | undefined {
  for (const selector of selectors) {
    const el = root.querySelector(selector);
    const html = sanitizeLegacySubSnippetHtml(String(el?.innerHTML ?? '').trim());
    if (html) return html;
  }
  return undefined;
}

const LEGACY_SUB_SNIPPET_CLASS_MAP: Record<string, string[]> = {
  'mcss-list_compact_head_txt-cssm': ['md-sub-compact-head-text'],
  'mcss-youneedapin-cssm': ['md-sub-pin-required'],
  'mcss-ic_clprt_lists-cssm': ['md-sub-inline-icon'],
  'mcss-ic_msg_time-cssm': ['md-sub-time-icon'],
  'mcss-ic_str-cssm': ['md-sub-status-text'],
};

function normalizedSubSnippetClasses(classValue: string): string[] {
  const next: string[] = [];
  for (const token of classValue.split(/\s+/).filter(Boolean)) {
    const mapped = LEGACY_SUB_SNIPPET_CLASS_MAP[token];
    if (mapped) {
      next.push(...mapped);
      continue;
    }
    if (token.startsWith('mcss-')) continue;
    next.push(token);
  }
  return Array.from(new Set(next));
}

function sanitizeLegacySubSnippetHtml(html: string | undefined): string | undefined {
  const raw = String(html ?? '').trim();
  if (!raw) return undefined;

  if (typeof DOMParser !== 'undefined') {
    const doc = new DOMParser().parseFromString(`<div>${raw}</div>`, 'text/html');
    const root = doc.body.firstElementChild;
    if (!root) return raw;
    root.querySelectorAll<HTMLElement>('[class]').forEach(el => {
      const classes = normalizedSubSnippetClasses(el.getAttribute('class') || '');
      if (classes.length) el.setAttribute('class', classes.join(' '));
      else el.removeAttribute('class');
    });
    return root.innerHTML.trim() || undefined;
  }

  return raw.replace(/\sclass=(["'])(.*?)\1/g, (_match, quote: string, classValue: string) => {
    const classes = normalizedSubSnippetClasses(classValue);
    return classes.length ? ` class=${quote}${classes.join(' ')}${quote}` : '';
  }).trim() || undefined;
}

function textFromLegacyHtml(html: string | undefined): string | undefined {
  const raw = String(html ?? '').trim();
  if (!raw) return undefined;

  if (typeof DOMParser !== 'undefined') {
    const doc = new DOMParser().parseFromString(raw, 'text/html');
    return cleanPreviewText(doc.body.textContent);
  }

  return cleanPreviewText(raw.replace(/<[^>]*>/g, ' '));
}

function legacyBodyTextareaMsgText(legacySub: any): string | undefined {
  const bodyTextarea = legacySub?.msg_inputs?.body_textarea;
  if (!Array.isArray(bodyTextarea)) return undefined;
  const bodyValue = bodyTextarea[1];
  if (bodyValue && typeof bodyValue === 'object') {
    return firstString((bodyValue as any).msg_text, (bodyValue as any).text, (bodyValue as any).html);
  }
  return firstString(bodyValue);
}

function documentTitleFallback(): string | undefined {
  const title = getMsgDeskRuntime().title;
  return title.replace(/^Msgdsk\.com\s*-\s*/i, '') || undefined;
}

function collectMainCounts(msgData: any): Record<string, unknown> | undefined {
  if (!msgData) return undefined;
  const counts: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(msgData)) {
    if (key.startsWith('count_') || key.endsWith('count') || key === 'answerscount') {
      counts[key] = value;
    }
  }
  return Object.keys(counts).length ? counts : undefined;
}

function collectMainFlags(msgData: any): Record<string, unknown> | undefined {
  if (!msgData) return undefined;
  const flags: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(msgData)) {
    if (key.startsWith('is_') || key.startsWith('has_') || key.startsWith('no') || key === 'timeblocked') {
      flags[key] = value;
    }
  }
  if (msgData.is_values && typeof msgData.is_values === 'object') flags.is_values = msgData.is_values;
  return Object.keys(flags).length ? flags : undefined;
}

function deriveMainState(templateData: TemplateDataState, legacyData: LegacyDataState): Omit<MsgObjMainState, 'templateData'> {
  const runtimeMsgData = readRuntimeMainMsgData();
  const syncedMsgData = legacyData.runtime?.msgData;
  const phpModelMsgData = legacyData.phpModel?.model?.main;
  const hiddenMsgData = legacyData.msges?.subs?.[0];
  const msgData = runtimeMsgData || syncedMsgData || phpModelMsgData || hiddenMsgData;
  const initData = legacyData.init;
  const msgInputs = readRuntimeMsgInputs(0) || legacyData.runtime?.msgInputs || msgData?.msg_inputs || {};
  const bodyInputs = Array.isArray(msgInputs.body) ? msgInputs.body : [];
  const bodyTextarea = Array.isArray(msgInputs.body_textarea) ? msgInputs.body_textarea : [];
  const source: MsgObjMainState['source'] = runtimeMsgData || syncedMsgData ? 'legacy-runtime' : (phpModelMsgData ? 'php-model' : (hiddenMsgData ? 'hidden-json' : 'templateData'));

  const subject = firstString(msgData?.subject, bodyInputs[1], msgData?.namekey_subject);
  const body = firstNonEmptyString(
    msgData?.body,
    msgData?.text,
    bodyTextarea[1],
    bodyTextarea[2],
    bodyTextarea[3],
    phpModelMsgData?.body,
    phpModelMsgData?.text,
    hiddenMsgData?.body,
    hiddenMsgData?.text,
  );
  const bodyHtml = firstNonEmptyString(
    msgData?.html,
    msgData?.body_html,
    msgData?.txt_html,
    phpModelMsgData?.html,
    phpModelMsgData?.body_html,
    phpModelMsgData?.txt_html,
    hiddenMsgData?.html,
    hiddenMsgData?.body_html,
    hiddenMsgData?.txt_html,
  );
  const title = firstString(initData?.title, subject, documentTitleFallback(), msgData?.name_key);
  const nameKey = firstString(msgData?.name_key, bodyTextarea[0], bodyTextarea[3]);
  const codeKey = firstString(msgData?.code_key, msgData?.codekey, bodyInputs[0]);
  const facadeEmail = msgData?.facade?.email && typeof msgData.facade.email === 'object'
    ? msgData.facade.email
    : {};

  const semanticFields: Record<string, unknown> = {
    subject,
    body,
    bodyHtml,
    title,
    datetime: firstDefined(msgData?.datetime, msgData?.create_time, msgData?.created_time, msgData?.timestart_str, msgData?.timestart),
    author: nameKey,
    codeKey,
    nameKey,
    avatarPath: firstString(msgData?.avatar_path, msgData?.avatarPath),
    msgId: firstDefined(msgData?.msg_id, msgData?.id),
    msgType: firstString(msgData?.msg_type, msgData?.msgtype),
    isHtml: typeof msgData?.is_html === 'boolean' ? msgData.is_html : undefined,
    isMsg: typeof msgData?.is_msg === 'boolean' ? msgData.is_msg : undefined,
    isLocked: typeof initData?.is_locked === 'boolean' ? initData.is_locked : undefined,
    email: {
      to: facadeEmail.to ?? msgData?.email_to ?? '',
      from: facadeEmail.from ?? msgData?.email_from ?? '',
      fromDisplay: firstString(facadeEmail.from_display) ?? '',
      message: facadeEmail.message && typeof facadeEmail.message === 'object'
        ? facadeEmail.message
        : null,
    },
    isEmpty: Array.isArray(msgData?.is_empty) ? { subject: !!msgData.is_empty[0], body: !!msgData.is_empty[1] } : undefined,
    counts: collectMainCounts(msgData),
    flags: collectMainFlags(msgData),
    inputs: msgInputs,
    defaults: msgData?.defaults,
    legacy: {
      msgData,
      initData,
    },
    source,
  };

  return {
    source,
    subject: semanticFields.subject as string | undefined,
    body: semanticFields.body as string | undefined,
    bodyHtml: semanticFields.bodyHtml as string | undefined,
    title: semanticFields.title as string | undefined,
    datetime: semanticFields.datetime as string | number | undefined,
    author: semanticFields.author as string | undefined,
    codeKey: semanticFields.codeKey as string | undefined,
    nameKey: semanticFields.nameKey as string | undefined,
    avatarPath: semanticFields.avatarPath as string | undefined,
    msgId: semanticFields.msgId as string | number | undefined,
    msgType: semanticFields.msgType as string | undefined,
    isHtml: semanticFields.isHtml as boolean | undefined,
    isMsg: semanticFields.isMsg as boolean | undefined,
    isLocked: semanticFields.isLocked as boolean | undefined,
    email: semanticFields.email as MsgObjMainState['email'],
    isEmpty: semanticFields.isEmpty as MsgObjMainState['isEmpty'],
    counts: semanticFields.counts as Record<string, unknown> | undefined,
    flags: semanticFields.flags as Record<string, unknown> | undefined,
    inputs: semanticFields.inputs as Record<string, unknown> | undefined,
    defaults: semanticFields.defaults as Record<string, unknown> | undefined,
    legacy: semanticFields.legacy as MsgObjMainState['legacy'],
    fields: {
      ...semanticFields,
      text: templateData.text,
      attrs: templateData.attrs,
    },
  };
}

export function selectMainState(state: SubsStoreState): MsgObjMainState {
  return {
    ...deriveMainState(state.templateData, state.legacyData),
    templateData: state.templateData,
  };
}

function currentMainMsgData(state: SubsStoreState): any {
  return readRuntimeMainMsgData()
    || state.legacyData.runtime?.msgData
    || state.legacyData.phpModel?.model?.main
    || state.legacyData.msges?.subs?.[0]
    || {};
}

// Stage 2b: shared model-first source for section/folder counts
// (count_subs_obj etc.). Components must not build their own fallback chains.
export function selectMainMsgCounts(state: SubsStoreState): any {
  return currentMainMsgData(state);
}

// TWO-isMsg-SCOPES (§0.16): the ONE canonical desk/main identity. Describes
// the message or answer whose desk is OPEN — never the editor target (that is
// EditorSessionContext's separate contract). Fail-closed: an absent or
// unrecognized is_msg means "answer desk" so no message-only affordance can
// leak while data is incomplete. SSR-deterministic: the boot payload's
// subs[0].is_msg reaches the same fallback chain on server and client.
export interface DeskIdentity {
  /** true only when the open desk is positively a named message. */
  mainIsMsg: boolean;
  /** Answers cannot create or hold submessages; only message desks can. */
  canHaveSubmessages: boolean;
}

export function selectDeskIdentity(state: SubsStoreState): DeskIdentity {
  const mainIsMsg = boolFromLegacy(currentMainMsgData(state)?.is_msg) === true;
  return { mainIsMsg, canHaveSubmessages: mainIsMsg };
}

/** Shared main-title fallback: stored subject/title first, then the desk's
 * stable identity (namekey for a named message, codekey for an answer). */
export function selectMainHeadingText(state: SubsStoreState): string {
  const main = selectMainState(state);
  const subject = firstNonEmptyString(main.subject, main.title);
  if (subject) return subject;
  return selectDeskIdentity(state).mainIsMsg
    ? firstNonEmptyString(main.nameKey, main.author, main.codeKey) || ''
    : firstNonEmptyString(main.codeKey) || '';
}

function cleanDocumentTitlePart(value: unknown): string {
  if (value === undefined || value === null) return '';
  return String(value).replace(/^\s+|\s+$/g, '');
}

/**
 * The browser title is the same signed identity shown by the main headline:
 * "@ namekey" for a named message and ":: subject" for an answer. This is a
 * live selector so an in-place main-entity edit updates the title without a
 * reload. Do not use String#trim here: the active legacy runtime still carries
 * the known broken global trim polyfill (README_CURRENT_TASKS §0.17).
 */
export function selectDeskDocumentTitle(state: SubsStoreState): string {
  const main = selectMainState(state);
  const { mainIsMsg } = selectDeskIdentity(state);
  const candidates = mainIsMsg
    ? [main.nameKey, main.author, main.title, main.codeKey]
    : [main.subject, main.title, main.codeKey];
  const text = candidates.map(cleanDocumentTitlePart).find(Boolean) || 'Message desk';
  return `${mainIsMsg ? '@' : '::'} ${text}`;
}

const FOLDER_COUNT_INDEX: Record<string, number> = { drafts: 2, trash: 3, favs: 4 };

// Prefer the LIVE count_subs array: legacy maintains it in place on every
// toggle/move (_subs_count), while count_subs_obj and the flat count_* fields
// are boot snapshots it never updates. Presence of the array (not its
// non-zeroness) decides, so a genuine drop to [0,0] is honoured.
function sectionCountForMode(counts: any, mode: string, sectionId: 0 | 1): number {
  const folderIndex = FOLDER_COUNT_INDEX[mode];
  if (folderIndex !== undefined) {
    const live = counts?.count_subs?.[folderIndex];
    if (Array.isArray(live)) return Number(live[sectionId] || 0);
    const snapshot = counts?.count_subs_obj?.[mode];
    if (Array.isArray(snapshot)) return Number(snapshot[sectionId] || 0);
    return 0;
  }
  const live = counts?.count_subs?.[0];
  if (Array.isArray(live)) return Number(live[sectionId] || 0);
  return sectionId === 1
    ? Number(counts?.count_submsges || counts?.count_allsubmsges || 0)
    : Number(counts?.count_answers || counts?.count_allanswers || 0);
}

export function selectFolderSectionCount(
  state: SubsStoreState,
  mode: 'drafts' | 'trash' | 'favs',
  isSubmsges: boolean,
): number {
  return sectionCountForMode(currentMainMsgData(state), mode, isSubmsges ? 1 : 0);
}

/**
 * One paging view-model per root section: the live legacy paging summary when
 * the runtime has projected this mode, else a deterministic fallback computed
 * from boot counts (identical on the server and the first client render, so
 * SSR/hydration stay in sync). Panels must not build their own page math.
 */
export function selectSectionPaging(
  state: SubsStoreState,
  isSubmsges: boolean,
  mode?: string,
): SectionPagingSummary {
  const section: SubsSection = isSubmsges ? 'submsges' : 'answers';
  const projectionMode = state.renderProjection.mode;
  const panelMode = mode || projectionMode;
  const live = panelMode === projectionMode ? state.renderProjection.paging?.[section] : undefined;
  if (live) return live;

  const counts = currentMainMsgData(state);
  const settings = selectPageSettingsState(state);
  const sectionId = isSubmsges ? 1 : 0;
  const total = sectionCountForMode(counts, panelMode, sectionId);
  const pageSize = Math.max(1, panelMode === 'msg'
    ? (isSubmsges ? settings.viewCountSubmsgesFirstPage : settings.viewCountAnswersFirstPage)
    : settings.viewCountMenuTabs);
  const currentPage = Number(state.renderProjection.currentPage[section] || 1);
  const loaded = selectRootFeedIds(state, isSubmsges, panelMode).length;
  const shownThrough = Math.min(total, (currentPage - 1) * pageSize + loaded);
  const moreCount = Math.max(0, total - shownThrough);
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  return { currentPage, totalPages, total, shownThrough, moreCount, pageSize };
}

function legacyAttachmentEntriesFromMainData(msgData: any): unknown[] {
  if (Array.isArray(msgData?.attaches1)) return msgData.attaches1;
  const attsArr = msgData?.atts_arr;
  if (!Array.isArray(attsArr)) return [];

  const wrappedItems = Array.isArray(attsArr[1]) ? attsArr[1] : attsArr;
  return wrappedItems
    .map((item: unknown) => {
      if (!Array.isArray(item)) return item;
      return Array.isArray(item[0]) ? item[0] : item;
    })
    .filter(Boolean);
}

function legacyAttachmentToState(entry: unknown, index: number): MainAttachmentState | null {
  if (!Array.isArray(entry)) return null;
  const paths = Array.isArray(entry[0]) ? entry[0] : [];
  const meta = Array.isArray(entry[2]) ? entry[2] : [];
  const dimensions = Array.isArray(meta[2]) ? meta[2] : [];
  const isAvatar = entry[3] === true;
  const src = firstString(paths[0]);
  if (!src || isAvatar) return null;

  const thumbSrc = firstString(paths[1], paths[0]) || src;
  const filename = firstNonEmptyString(entry[1], paths[3], src.split('/').pop());
  const title = firstNonEmptyString(meta[0], filename);
  const description = firstNonEmptyString(meta[1]);
  const width = firstNumber(dimensions[0]);
  const height = firstNumber(dimensions[1]);
  const size = firstNumber(meta[3]);
  const mime = attachmentMimeForAttachment(filename) ?? attachmentMimeForAttachment(src);
  const mediaKind = attachmentMediaKind(filename || src);

  return {
    id: firstString(paths[2], paths[3], index) || String(index),
    src,
    thumbSrc,
    filename,
    title,
    description,
    width,
    height,
    size,
    mediaKind,
    mime,
    legacyIndex: index,
  };
}

export function selectMainAttachmentsState(state: SubsStoreState): MainAttachmentState[] {
  // Stage 2b: live model first (post-2a it exists before the first ready
  // render); the span-parsed copy is the last fallback until 2d removes it.
  const candidates = [
    readRuntimeMainMsgData(),
    state.legacyData.runtime?.msgData,
    state.legacyData.phpModel?.model?.main,
    state.legacyData.msges?.subs?.[0],
  ];

  for (const msgData of candidates) {
    const attachments = legacyAttachmentEntriesFromMainData(msgData)
      .map((entry, index) => legacyAttachmentToState(entry, index))
      .filter((attachment): attachment is MainAttachmentState => !!attachment);
    if (attachments.length) return attachments;
  }

  return [];
}

function legacyBoolean(value: unknown): boolean {
  if (value === true || value === 1) return true;
  if (typeof value === 'string') {
    const normalized = value.trim().toLowerCase();
    return normalized === '1' || normalized === 'true' || normalized === 'yes';
  }
  return false;
}

function legacyPinUnlocked(value: unknown): boolean {
  return value !== '-1';
}

function numericDefault(value: unknown, fallback: number): number {
  const numberValue = Number(value);
  return Number.isFinite(numberValue) && numberValue > 0 ? numberValue : fallback;
}

function defaultViewCountArray(msgData: any): unknown[] {
  const defaults = msgData?.defaults || {};
  const paging = defaults?.paging || {};
  const candidates = [
    defaults.view_count_arr,
    defaults.view_count,
    paging.view_count_arr,
    paging.view_count,
  ];
  return candidates.find(Array.isArray) || [];
}

/** BLOCKED-ACCESS-MODEL: minimal wire-shape guard for an emitted grant. */
export function isAccessGrant(value: unknown): value is MsgAccessGrant {
  if (!value || typeof value !== 'object') return false;
  const grant = value as Record<string, unknown>;
  return typeof grant.reason === 'string'
    && typeof grant.challenge === 'string'
    && !!grant.capabilities && typeof grant.capabilities === 'object'
    && !!grant.viewer && typeof grant.viewer === 'object';
}

/**
 * The main-message semantic grant. Prefers the server-emitted access_grant
 * (grant-aware boots); falls back to deriving the same shape from the legacy
 * '-1' pin markers and is_values so pre-grant payloads keep working.
 */
export function selectAccessGrant(state: SubsStoreState): MsgAccessGrant {
  const msgData = currentMainMsgData(state);
  if (isAccessGrant(msgData?.access_grant)) return msgData.access_grant;

  const isValues = msgData?.is_values || {};
  const forbidSubs = Array.isArray(isValues.is_forbid_subs) ? isValues.is_forbid_subs : [];
  const editUnlocked = legacyPinUnlocked(msgData?.edit_pin);
  const showUnlocked = legacyPinUnlocked(msgData?.show_pin) || editUnlocked;
  const timeLocked = legacyBoolean(msgData?.timeblocked) || (Array.isArray(msgData?.timeblocked) && msgData.timeblocked.length > 0);
  const forbidAnswers = legacyBoolean(forbidSubs[0]);
  const forbidMsges = legacyBoolean(forbidSubs[1]);

  const timeblockedPair = Array.isArray(msgData?.timeblocked) ? msgData.timeblocked : [];

  return {
    reason: !showUnlocked ? (timeLocked ? 'time' : 'show-pin') : !editUnlocked ? 'edit-pin' : 'none',
    challenge: !showUnlocked ? (timeLocked ? 'time-info' : 'inline-show-pin') : 'none',
    capabilities: {
      can_view_content: showUnlocked,
      can_reply: showUnlocked && !forbidAnswers,
      // POSSESSION-MODEL: named creation is possessor-only (mirrors the
      // server grant; the fallback keeps pre-grant payloads consistent).
      can_create_named_message: editUnlocked && !forbidMsges,
      can_edit: editUnlocked,
      can_use_drafts_trash: editUnlocked,
      can_check_or_move: editUnlocked,
      can_see_parents: !legacyBoolean(isValues.is_hide_parents),
    },
    viewer: {
      is_logged: false,
      is_owner: false,
      msg_id: '',
      code_key: '',
      name_key: '',
      members: 'off',
    },
    ...(timeLocked && timeblockedPair.length > 0
      ? { time_side: legacyBoolean(timeblockedPair[0]) ? 'early' as const : 'elapsed' as const }
      : {}),
  };
}

/**
 * POSSESSION-MODEL: the desk's own author-state — who the visitor acts AS on
 * this page. `possessed` mirrors grant.capabilities.can_edit (edit-unlocked =
 * acting as the msg itself); `via` distinguishes the pinless "editable by
 * everyone" state (edit pin ''/'0') from a pin/editCode-unlocked possession.
 */
export interface DeskPossessionState {
  possessed: boolean;
  via: 'open' | 'pin' | null;
  showUnlocked: boolean;
  /** How viewing is unlocked: 'open' = no show pin set, 'pin' = a matched
   * pin opened it, null = still show-locked. */
  showVia: 'open' | 'pin' | null;
}

export function selectDeskPossession(state: SubsStoreState): DeskPossessionState {
  const grant = selectAccessGrant(state);
  const possessed = grant.capabilities.can_edit;
  const msgData = currentMainMsgData(state);
  const editPin = msgData?.edit_pin;
  const pinless = editPin === '0' || editPin === 0 || editPin === '';
  const showUnlocked = grant.capabilities.can_view_content;
  const showPin = msgData?.show_pin;
  const showPinless = showPin === '0' || showPin === 0 || showPin === '' || showPin === '1' || showPin === 1;
  return {
    possessed,
    via: possessed ? (pinless ? 'open' : 'pin') : null,
    showUnlocked,
    showVia: showUnlocked ? (showPinless ? 'open' : 'pin') : null,
  };
}

export function selectAccessState(state: SubsStoreState): MsgAccessState {
  // Thin compatibility wrapper over the semantic grant: the four legacy
  // booleans every existing surface consumer reads.
  const grant = selectAccessGrant(state);
  return {
    showUnlocked: grant.capabilities.can_view_content,
    editUnlocked: grant.capabilities.can_edit,
    // Any creation block (forbid flag OR members gate) hides the legacy
    // buttons; grant-aware surfaces read selectAccessGrant for the challenge.
    forbidAnswers: grant.capabilities.can_view_content && !grant.capabilities.can_reply,
    forbidSubmsges: grant.capabilities.can_view_content && !grant.capabilities.can_create_named_message,
  };
}

/**
 * FAVS-IDENTITY: whether the row STARS and bulk favorite actions show for
 * this viewer. Owner state (desk edit-unlocked) toggles the desk's bucket; a
 * logged visitor on an edit-locked desk toggles their OWN bucket (server-side
 * session rule) and reviews it on their own desk; anonymous / show-pin-only
 * viewers on an edit-locked desk see no favorites at all. The Bookmarks TAB
 * is stricter — owner-state only (menu.can.favs / FAVS-PRIVACY).
 */
export function selectFavsAllowed(state: SubsStoreState): boolean {
  const grant = selectAccessGrant(state);
  return grant.capabilities.can_edit || Boolean(grant.viewer?.is_logged);
}

/**
 * PARENTS-MENU: project the serialized parents chain once into a typed
 * ordered ParentLink[]. The wire shape is msgdataApi msgParents(): a sparse
 * {number: [code_key, label, msg_type, is_msg, subject]} map (encoded [] when
 * empty) whose numbers count DOWN toward the root; legacy _html_parents_ul
 * rendered highest number first.
 * JS object iteration would silently reverse that (integer keys ascend), so
 * the sort is explicit. is_hide_parents is server-enforced at load time
 * (fields 79/80 zeroed, chain not fetched); the grant only mirrors it here.
 */
export function selectParentsMenu(state: SubsStoreState): ParentsMenuState {
  const msgData = currentMainMsgData(state);
  const grant = selectAccessGrant(state);

  const links: ParentLink[] = [];
  const raw = msgData?.parents;
  if (raw && typeof raw === 'object' && !Array.isArray(raw)) {
    Object.entries(raw as Record<string, unknown>)
      .map(([key, value]) => [Number(key), value] as [number, unknown])
      .filter(([number, value]) => Number.isFinite(number) && Array.isArray(value))
      .sort((a, b) => b[0] - a[0])
      .forEach(([number, value]) => {
        const pair = value as unknown[];
        const codeKey = firstNonEmptyString(pair[0]);
        if (!codeKey) return;
        links.push({
          codeKey,
          label: firstNonEmptyString(pair[1]) || codeKey,
          number,
          msgType: (firstNonEmptyString(pair[2]) ?? '').toLowerCase(),
          isMsg: legacyBoolean(pair[3]),
          subject: firstNonEmptyString(pair[4]) ?? '',
        });
      });
  }

  const countPair = msgData?.count_subs_obj?.parents;
  const count = Array.isArray(countPair)
    ? (firstNumber(countPair[0]) ?? 0) + (firstNumber(countPair[1]) ?? 0)
    : links.length;

  return {
    canSee: grant.capabilities.can_see_parents && grant.capabilities.can_view_content,
    count,
    links,
  };
}

export function selectPageSettingsState(state: SubsStoreState): MsgPageSettingsState {
  const msgData = currentMainMsgData(state);
  const isValues = msgData?.is_values || {};
  const foldSubs = Array.isArray(isValues.is_fold_subs) ? isValues.is_fold_subs : [];
  const viewCounts = defaultViewCountArray(msgData);

  return {
    foldAnswers: legacyBoolean(foldSubs[0]),
    foldSubmsges: legacyBoolean(foldSubs[1]),
    viewCountAnswersFirstPage: numericDefault(viewCounts[0], 20),
    viewCountSubmsgesFirstPage: numericDefault(viewCounts[1], 20),
    viewCountMenuTabs: numericDefault(viewCounts[2], 20),
  };
}

function deriveTemplateEditorInputs(templateData: TemplateDataState): Record<string, unknown> {
  const inputs: Record<string, unknown> = {};
  for (const [slot, attrs] of Object.entries(templateData.attrs)) {
    if (attrs.name || attrs.value !== undefined || attrs.type) {
      inputs[slot] = {
        name: attrs.name,
        type: attrs.type,
        value: attrs.value,
      };
    }
  }
  return inputs;
}

function deriveEditorInputs(templateData: TemplateDataState, legacyData: LegacyDataState): Record<string, unknown> {
  const templateInputs = deriveTemplateEditorInputs(templateData);
  const runtimeInputs = readRuntimeMsgInputs(0);
  const legacyInputs = runtimeInputs || legacyData.runtime?.msgInputs || legacyData.runtime?.msgData?.msg_inputs || legacyData.msges?.subs?.[0]?.msg_inputs;

  if (!legacyInputs || typeof legacyInputs !== 'object') return templateInputs;

  return {
    ...templateInputs,
    ...legacyInputs,
    templateSlots: templateInputs,
    source: 'legacy-runtime',
  };
}

export function selectEditorState(state: SubsStoreState): MsgObjEditorState {
  const templateData = state.templateData;
  return {
    inputs: deriveEditorInputs(templateData, state.legacyData),
    formInputs: templateData.editorFormInputs,
    formModel: templateData.editorFormModel,
    templateData,
  };
}

function isElementNode(node: LegacyParsedNode): node is LegacyParsedElementNode {
  return node.kind === 'element';
}

function mergeEditorFormInputs(
  fallbackInputs: Record<string, EditorFormInputState>,
  primaryInputs: Record<string, EditorFormInputState>,
): Record<string, EditorFormInputState> {
  const merged = { ...fallbackInputs };
  for (const [name, field] of Object.entries(primaryInputs)) {
    const current = merged[name];
    const valueIsUseful = field.value !== undefined;
    const stateIsUseful = field.checked !== undefined || field.selectedValues !== undefined || field.type === 'hidden';
    if (valueIsUseful || stateIsUseful || !current) merged[name] = field;
  }
  return merged;
}

function isVisualOnlyEditorFragmentSelector(selector: string): boolean {
  return selector.includes('mcss-new_to_msg_list-cssm')
    || selector.includes('mcss-avatar_div-cssm');
}

function isAttachmentGalleryFragmentSelector(selector: string): boolean {
  return selector.includes('mcss-att_glr-cssm')
    || selector.includes('mcss-attaches_gallery-cssm');
}

function parseEditorAttachmentsFromHtml(html: string): EditorAttachmentState[] {
  const root = getMsgDeskRuntime().createElement('div');
  if (!html || !root) return [];
  root.innerHTML = html;
  const labels = Array.from(root.querySelectorAll<HTMLElement>('.mcss-file_label-cssm'));

  return labels.map((label, index) => {
    const img = label.querySelector<HTMLImageElement>('img');
    const filename = label.querySelector<HTMLElement>('.mcss-file_name-cssm')?.textContent?.trim() || '';
    const progress = label.querySelector<HTMLElement>('.mcss-rm_file_2-cssm')?.textContent?.trim() || '';
    const remove = label.querySelector<HTMLElement>('.mcss-rm_file-cssm');
    const overlay = label.querySelector<HTMLElement>('.mcss-img_holder_2-cssm');
    const id = label.getAttribute('att_id') || undefined;

    return {
      id,
      src: img?.getAttribute('src') || 'css/cssimgs/attach.png',
      filename,
      progress,
      removable: !!id || !!remove?.getAttribute('onclick'),
      toggleable: !!id || !!overlay?.getAttribute('onclick'),
      isUploadPlaceholder: !id && index === 0,
      isAvatar: html.includes('mcss-avatar_glr-cssm'),
    };
  });
}

function hasRealEditorAttachments(attachments?: EditorAttachmentState[]): boolean {
  return !!attachments?.some(attachment => !attachment.isUploadPlaceholder && !!attachment.id);
}

function normalizeEditorAttachmentsUpdate(
  current: EditorAttachmentState[] | undefined,
  next: EditorAttachmentState[],
): EditorAttachmentState[] {
  if (hasRealEditorAttachments(current) && !hasRealEditorAttachments(next)) return current || [];
  return next;
}

function collectEditorAttachmentsFromRuntime(): EditorAttachmentState[] {
  const rough = getMsgDeskRuntime().facade.editorRough();
  const msgData = readRuntimeMainMsgData();
  const attsArr = rough?.atts_arr || msgData?.atts_arr;
  if (!Array.isArray(attsArr)) return [];

  const attachments: EditorAttachmentState[] = [{
    src: 'css/cssimgs/attach.png',
    filename: '',
    progress: '0%',
    removable: false,
    toggleable: false,
    isUploadPlaceholder: true,
  }];

  for (let index = 2; index < attsArr.length; index += 1) {
    const entry = attsArr[index];
    if (!Array.isArray(entry)) continue;
    const v2Entry = entry as typeof entry & { __v2Error?: string; __v2PreviewSrc?: string };
    const data = Array.isArray(entry[7]) ? entry[7] : [];
    const paths = Array.isArray(data[0]) ? data[0] : [];
    const src = firstString(paths[1], paths[0]);
    if (!src) continue;

    attachments.push({
      id: String(entry[0] || index),
      src: firstString(v2Entry.__v2PreviewSrc, src) || 'css/cssimgs/attach.png',
      filename: firstString(data[1], paths[3]),
      progress: entry[3] ? 'Uploading...' : '0%',
      removable: true,
      toggleable: true,
      active: entry[6] !== false,
      status: v2Entry.__v2Error ? 'error' : (entry[3] ? 'uploading' : 'done'),
      error: v2Entry.__v2Error ? String(v2Entry.__v2Error) : undefined,
    });
  }

  return hasRealEditorAttachments(attachments) ? attachments : [];
}

function normalizeHtmlId(input: string): string {
  if (!input) return '';
  let id = String(input).trim();
  if (id.startsWith('#')) id = id.slice(1);
  return id.replace(/['"]/g, '').trim();
}

function attrsToRecord(node: LegacyParsedElementNode): Record<string, string> {
  const out: Record<string, string> = {};
  for (const attr of node.attrs) out[attr.name] = attr.value;
  return out;
}

function domAttrsToRecord(node: HTMLElement): Record<string, string> {
  if (!node.getAttributeNames) return {};
  const out: Record<string, string> = {};
  for (const name of node.getAttributeNames()) {
    const value = node.getAttribute(name);
    if (value != null) out[name] = value;
  }
  return out;
}

function defaultChromeVisible(contract: string): boolean | undefined {
  if (contract === 'workspace.main') return true;
  if (
    contract === 'workspace.editor' ||
    contract === 'workspace.attachments' ||
    contract === 'workspace.favs' ||
    contract === 'workspace.trash' ||
    contract === 'workspace.drafts'
  ) return false;
  return undefined;
}

function extractElIdFromAttrs(attrs: Record<string, string>): number | null {
  const raw = normalizeHtmlId(attrs.id || '');
  if (!raw.startsWith('mcss-li_id-cssm_')) return null;
  const elId = parseInt(raw.replace('mcss-li_id-cssm_', ''), 10);
  return Number.isNaN(elId) ? null : elId;
}

function parseStyleString(value: string): Map<string, string> {
  const out = new Map<string, string>();
  for (const part of value.split(';')) {
    const i = part.indexOf(':');
    if (i < 0) continue;
    const name = part.slice(0, i).trim();
    const val = part.slice(i + 1).trim();
    if (name) out.set(name, val);
  }
  return out;
}

function styleMapToString(styleMap: Map<string, string>): string {
  const parts: string[] = [];
  for (const [k, v] of styleMap) parts.push(`${k}:${v}`);
  return parts.join(';');
}

function setClassOnAttrs(
  attrs: Record<string, string>,
  op: 'add' | 'remove' | 'toggle',
  cls: string
): Record<string, string> {
  const classes = new Set((attrs.class || '').split(/\s+/).filter(Boolean));
  for (const one of cls.split(/\s+/).filter(Boolean)) {
    if (op === 'add') classes.add(one);
    else if (op === 'remove') classes.delete(one);
    else if (classes.has(one)) classes.delete(one);
    else classes.add(one);
  }
  return { ...attrs, class: ' ' + Array.from(classes).join(' ') + ' ' };
}

function setCssOnAttrs(
  attrs: Record<string, string>,
  prop: string,
  value: string | null | undefined
): Record<string, string> {
  const styleMap = parseStyleString(attrs.style || '');
  if (value === '' || value == null) styleMap.delete(prop);
  else styleMap.set(prop, String(value));
  const nextStyle = styleMapToString(styleMap);
  const next = { ...attrs };
  if (nextStyle) next.style = nextStyle;
  else delete next.style;
  return next;
}

function hasClassOnAttrs(attrs: Record<string, string>, cls: string): boolean {
  return (attrs.class || '').split(/\s+/).filter(Boolean).includes(cls);
}

function getCssFromAttrs(attrs: Record<string, string>, prop: string): string {
  return parseStyleString(attrs.style || '').get(prop) || '';
}

function getTextFromNode(nodeId: string, nodesById: Record<string, SubsNodeData>): string {
  const node = nodesById[nodeId];
  if (!node) return '';
  if (node.kind === 'text') return node.text;
  return node.childIds.map(childId => getTextFromNode(childId, nodesById)).join('');
}

function findElementNodeId(
  nodeIds: string[],
  state: SubsStoreState,
  predicate: (node: SubsElementNodeData) => boolean
): string | undefined {
  for (const nodeId of nodeIds) {
    const node = state.nodesById[nodeId];
    if (!node) continue;
    if (node.kind === 'element') {
      if (predicate(node)) return node.id;
      const childMatch = findElementNodeId(node.childIds, state, predicate);
      if (childMatch) return childMatch;
    }
  }
  return undefined;
}

function textByHtmlId(state: SubsStoreState, htmlId: string): string | undefined {
  const nodeId = state.htmlIdToNodeId[htmlId];
  if (!nodeId) return undefined;
  const node = state.nodesById[nodeId];
  if (!node || node.kind !== 'element') return undefined;
  const text = node.childIds.map(childId => getTextFromNode(childId, state.nodesById)).join('').trim();
  return text || undefined;
}

function classListFromNode(node: SubsElementNodeData): string[] {
  return (node.attrs.class || '').split(/\s+/).filter(Boolean);
}

function textByClassMatch(
  state: SubsStoreState,
  rootNodeIds: string[],
  predicate: (className: string) => boolean
): string | undefined {
  const nodeId = findElementNodeId(rootNodeIds, state, node => classListFromNode(node).some(predicate));
  if (!nodeId) return undefined;
  const node = state.nodesById[nodeId];
  if (!node || node.kind !== 'element') return undefined;
  const text = node.childIds.map(childId => getTextFromNode(childId, state.nodesById)).join('').trim();
  return text || undefined;
}

function rootSlotForSub(state: SubsStoreState, rootNodeIds: string[]): string | undefined {
  const nodeId = findElementNodeId(rootNodeIds, state, node => /^mcss-li_id-cssm_\d+$/.test(node.htmlId || ''));
  const node = nodeId ? state.nodesById[nodeId] : undefined;
  return node?.kind === 'element' ? node.slot : undefined;
}

function hiddenSubData(state: SubsStoreState, elId: number): any | undefined {
  // Stage 2b: live model first; the span-parsed copy (latest response only)
  // remains the fallback until 2c derives row nodes from the model.
  const runtimeSub = readRuntimeSubMsgData(elId);
  if (runtimeSub) return runtimeSub;
  const subs = state.legacyData.msges?.subs;
  if (!subs) return undefined;
  return subs[elId] ?? subs[String(elId)];
}

function idsFromLegacyTreeItems(items: any): number[] {
  if (!Array.isArray(items)) return [];
  return items
    .map((item: any) => item?.[0]?.[0])
    .filter((id: unknown): id is number => typeof id === 'number');
}

function appendUniqueId(ids: number[], elId: number): void {
  if (elId > 0 && !ids.includes(elId)) ids.push(elId);
}

function idsFromRuntimeTreeKeys(subsTree: any): number[] {
  if (!subsTree || typeof subsTree !== 'object') return [];

  return Object.keys(subsTree)
    .filter(key => /^\d+$/.test(key))
    .map(key => Number(key))
    .sort((a, b) => b - a)
    .map(subNumber => {
      try {
        return Number(subsTree[subNumber]?.el_id?.() || 0);
      } catch {
        return 0;
      }
    })
    .filter((id, index, ids): id is number =>
      id > 0 && ids.indexOf(id) === index
    );
}

function idsFromRuntimeTreeObject(treeObj: any, isSubmsges: boolean, countMode: 'visible' | 'loaded' = 'visible'): number[] {
  try {
    const subsTree = treeObj?.subs?.(isSubmsges);
    if (!subsTree) return [];

    const ids: number[] = [];
    let subNumber = Number(treeObj?.start_id?.(isSubmsges) || 0);
    let count = countMode === 'loaded'
      ? Number(treeObj?.loaded?.(isSubmsges) || treeObj?.showed?.(false, isSubmsges) || 0)
      : Number(treeObj?.showed?.(false, isSubmsges) || treeObj?.loaded?.(isSubmsges) || 0);
    let guard = 0;

    while (subNumber > 0 && guard < 1000) {
      const treeNode = subsTree[subNumber];
      if (!treeNode || typeof treeNode.el_id !== 'function') break;

      const elId = Number(treeNode.el_id());
      appendUniqueId(ids, elId);

      count -= 1;
      if (count <= 0) break;

      subNumber = Number(treeNode.next_id?.() || 0);
      guard += 1;
    }

    if (countMode === 'loaded') {
      for (const elId of idsFromRuntimeTreeKeys(subsTree)) {
        appendUniqueId(ids, elId);
      }
      if (ids.length) return ids;
    }

    if (ids.length) return ids;
    const keyedIds = idsFromRuntimeTreeKeys(subsTree);
    return keyedIds.length ? keyedIds : idsFromLegacyTreeItems(subsTree);
  } catch {
    return [];
  }
}

function legacySectionId(section: LegacySectionInput): 0 | 1 {
  return section === 'submsges' || section === 1 ? 1 : 0;
}

function checkedIdsFromRuntimeTreeObject(treeObj: any, isSubmsges: boolean): number[] | null {
  try {
    const checkedData = treeObj?.checked_subs?.()?.[isSubmsges ? 1 : 0];
    const checkedMap = checkedData?.[2];
    if (!checkedMap || typeof checkedMap !== 'object') return null;

    return Object.keys(checkedMap)
      .filter(key => checkedMap[key] && /^\d+$/.test(key))
      .map(key => Number(key))
      .filter((id, index, ids): id is number =>
        id > 0 && ids.indexOf(id) === index
      );
  } catch {
    return null;
  }
}

function normalizeRootTreeMode(mode: string) {
  return ['answers', 'submsges', 'subs'].includes(mode) ? 'msg' : mode;
}

function isActiveProjectionMode(mode?: string): boolean {
  return !mode || mode === 'active' || mode === 'msg' || mode === 'answers' || mode === 'submsges' || mode === 'subs';
}

function runtimeRootIdsForMode(isSubmsges: boolean, mode = 'msg'): number[] {
  try {
    const tree = getMsgDeskRuntime().facade.subsTree(normalizeRootTreeMode(mode));
    return tree ? idsFromRuntimeTreeObject(tree, isSubmsges) : [];
  } catch {
    return [];
  }
}

function hiddenJsonRootIds(state: SubsStoreState, isSubmsges: boolean): number[] {
  const rootTree = state.legacyData.msges?.subs_tree;
  if (!Array.isArray(rootTree)) return [];
  return idsFromLegacyTreeItems(rootTree[isSubmsges ? 1 : 0]);
}

function idsForSection(state: SubsStoreState, ids: number[], isSubmsges: boolean): number[] {
  return ids.filter(elId => {
    const runtimeSub = readRuntimeSubMsgData(elId);
    const storedSub = hiddenSubData(state, elId);
    const section = boolFromLegacy(firstDefined(runtimeSub?.is_submsges_section, storedSub?.is_submsges_section));
    return section === undefined || section === isSubmsges;
  });
}

export function selectRootFeedIds(state: SubsStoreState, isSubmsges: boolean, mode = 'msg'): number[] {
  const section: SubsSection = isSubmsges ? 'submsges' : 'answers';

  // FOLDER MODES (favs/drafts/trash) are read from their OWN legacy tree, keyed
  // by mode+section. renderProjection.rootIds is a single shared object that
  // mirrors ONLY the current projection mode's sections — reading it from a
  // folder panel would leak the active/main subs into the folder (trash showing
  // active rows) and can render one section's list under the other. The
  // mode-specific runtime tree is authoritative for which subs AND which
  // section. rootIds is used only as the SSR/pre-runtime seed, and only while
  // the store is actually projecting this same folder mode.
  if (mode === 'favs' || mode === 'drafts' || mode === 'trash') {
    const runtimeIds = runtimeRootIdsForMode(isSubmsges, mode);
    if (runtimeIds.length) return idsForSection(state, runtimeIds, isSubmsges);
    if (state.renderProjection.mode === mode) {
      const seeded = state.renderProjection.rootIds[section];
      if (seeded.length) return idsForSection(state, seeded, isSubmsges);
    }
    const auxiliaryIds = state.auxiliaryRootIds?.[mode]?.[section] || [];
    if (auxiliaryIds.length) return idsForSection(state, auxiliaryIds, isSubmsges);
    return [];
  }

  const projectedIds = state.renderProjection.rootIds[section];
  if (projectedIds.length) return idsForSection(state, projectedIds, isSubmsges);

  const treeMode = normalizeRootTreeMode(mode);
  const runtimeIds = runtimeRootIdsForMode(isSubmsges, treeMode);
  if (runtimeIds.length) return idsForSection(state, runtimeIds, isSubmsges);
  if (treeMode !== 'msg') return [];

  const hiddenIds = hiddenJsonRootIds(state, isSubmsges);
  return idsForSection(state, hiddenIds, isSubmsges);
}

export function selectCheckedSubIds(state: SubsStoreState, section: LegacySectionInput = 'answers', mode = state.renderProjection.mode): number[] {
  const sectionId = legacySectionId(section);
  const semanticIds = Object.entries(state.subStates)
    .filter(([, subState]) => subState.isFlagBlue)
    .map(([id]) => Number(id))
    .filter((id, index, ids): id is number => id > 0 && ids.indexOf(id) === index);
  try {
    const treeObj = getMsgDeskRuntime().facade.subsTree(mode || currentLegacyPagingMode());
    const runtimeIds = checkedIdsFromRuntimeTreeObject(treeObj, sectionId === 1);
    if (runtimeIds !== null) {
      const reconciledIds = runtimeIds.filter(id => state.subStates[id]?.isFlagBlue !== false);
      return idsForSection(state, [...new Set([...reconciledIds, ...semanticIds])], sectionId === 1);
    }
  } catch {
    // Fall through to TreeStore semantic state.
  }

  return idsForSection(state, semanticIds, sectionId === 1);
}

function normalizeMoveCheckedOptions(options?: MoveCheckedOptions, fallbackSection: LegacySectionInput = 'answers'): Required<MoveCheckedOptions> {
  return {
    section: options?.section ?? fallbackSection,
    mode: options?.mode || currentLegacyPagingMode(),
    reason: options?.reason || '',
  };
}

function normalizeProjectionMode(mode?: string): string {
  if (!mode) return 'msg';
  return mode;
}

function projectionModeFromMenuSwitcher(switcherId: unknown): string | null {
  switch (Number(switcherId)) {
    case 0: return 'msg';
    case 5: return 'trash';
    case 6: return 'favs';
    case 8: return 'attachments';
    case 9: return 'answers';
    case 10: return 'submsges';
    case 13: return 'drafts';
    default: return null;
  }
}

function normalizeMoveProjectionMode(mode?: string): string {
  if (mode === 'subs') return 'msg';
  return normalizeProjectionMode(mode);
}

function runtimeChildIdsForSub(elId: number): Record<SubsSection, number[]> | null {
  try {
    const treeObj = getMsgDeskRuntime().facade.subsTree(elId);
    if (!treeObj) return null;
    return {
      answers: idsFromRuntimeTreeObject(treeObj, false, 'loaded'),
      submsges: idsFromRuntimeTreeObject(treeObj, true, 'loaded'),
    };
  } catch {
    return null;
  }
}

function childIdsForSub(state: SubsStoreState, elId: number): Record<SubsSection, number[]> {
  const runtimeChildren = runtimeChildIdsForSub(elId);
  if (runtimeChildren && (runtimeChildren.answers.length || runtimeChildren.submsges.length)) {
    return runtimeChildren;
  }

  const hiddenSub = hiddenSubData(state, elId);
  const hiddenTree = hiddenSub?.subs_tree;
  if (hiddenTree) {
    return {
      answers: idsFromLegacyTreeItems(hiddenTree[0]),
      submsges: idsFromLegacyTreeItems(hiddenTree[1]),
    };
  }

  const children = state.childrenMap[elId] || { answers: [], submsges: [] };
  return {
    answers: [...children.answers],
    submsges: [...children.submsges],
  };
}

function normalizeVariantValue(value: unknown): string {
  return String(value ?? '').trim().toLowerCase();
}

function resolveLegacySubVisualVariant(legacySub: any): SubVisualVariant {
  const viewType = normalizeVariantValue(firstDefined(legacySub?.view_type, legacySub?.viewtype));
  const msgType = normalizeVariantValue(firstDefined(legacySub?.msg_type, legacySub?.msgtype));
  const declared = normalizeVariantValue(firstDefined(
    legacySub?.visual_variant,
    legacySub?.visualVariant,
    legacySub?.variant,
    legacySub?.template,
    legacySub?.template_type,
  ));
  const values = [declared, viewType, msgType].filter(Boolean);
  const declaredSkinVariants = new Set<SubVisualVariant>([
    'cards',
    'chat',
    'contacts',
    'facebook',
    'flickr',
    'fullscreen',
    'gallery',
    'gmail',
    'hackernews',
    'legacystd',
    'hotmail',
    'instagram',
    'phpbb',
    'presentation',
    'tiles',
    'twitter',
    'wiki',
  ]);

  if (declaredSkinVariants.has(declared as SubVisualVariant)) return declared as SubVisualVariant;

  if (values.some(value => value === 'comments' || value === 'comment' || value === 'thread')) return 'comments';
  if (values.some(value => value === 'email' || value === 'mail' || value === 'mail_box')) return 'email';
  if (values.some(value =>
    value === 'card' ||
    value === 'cards' ||
    value === 'foto' ||
    value === 'photo' ||
    value === 'gallery' ||
    value === 'gallery_uno' ||
    value === 'gallery_height'
  )) return 'card';
  if (legacySub?.email_from && legacySub.email_from !== '0') return 'email';
  if (legacySub?.email_to && legacySub.email_to !== '0') return 'email';

  return 'compact';
}

export function selectSubRenderState(state: SubsStoreState, elId: number): SubRenderState | null {
  const rootNodeIds = state.nodesByElId[elId] || [];
  const runtimeSub = readRuntimeSubMsgData(elId);
  const storedSub = hiddenSubData(state, elId);
  const legacySub = runtimeSub && storedSub
    ? { ...storedSub, ...runtimeSub, msg_inputs: runtimeSub.msg_inputs || storedSub.msg_inputs }
    : runtimeSub || storedSub;
  const subState = state.subStates[elId] || defaultSubUiState();
  const children = childIdsForSub(state, elId);
  const legacyBodyHtml = legacyBodyTextareaMsgText(legacySub);
  const facade = legacySub?.facade || {};
  const facadeContent = facade.content || {};
  const facadeIdentity = facade.identity || {};
  const facadeTime = facade.time || {};
  const facadeAvatar = facade.avatar || {};
  const facadeAttachments = Array.isArray(facade.attachments?.items)
    ? facade.attachments.items.map((attachment: any, index: number) => ({
        id: firstDefined(attachment?.id, index + 1) as string | number,
        src: firstString(attachment?.src),
        thumbSrc: firstString(attachment?.thumb_src, attachment?.src),
        storedName: firstString(attachment?.stored_name),
        basePath: firstString(attachment?.base_path),
        filename: firstString(attachment?.filename),
        title: firstString(attachment?.title),
        description: firstString(attachment?.description),
        width: firstNumber(attachment?.width),
        height: firstNumber(attachment?.height),
        size: firstNumber(attachment?.size),
        storage: firstNumber(attachment?.storage),
        mediaKind: attachment?.media_kind === 'audio'
          ? 'audio'
          : attachment?.media_kind === 'image' ? 'image' : 'file',
        mime: firstString(attachment?.mime),
      }))
    : [];
  const codeKey = firstNonEmptyString(
    facadeIdentity.code_key,
    legacySub?.code_key,
    legacySub?.codekey,
  );

  if (!legacySub && !rootNodeIds.length) return null;

  const text = firstNonEmptyString(
    facadeContent.text,
    legacySub?.text,
    legacySub?.body,
    legacySub?.msg_text,
    legacySub?.txt,
    textFromLegacyHtml(firstNonEmptyString(
      facadeContent.html,
      legacySub?.html,
      legacySub?.body_html,
      legacySub?.txt_html,
    )),
    textFromLegacyHtml(legacyBodyHtml),
    legacySub?.msg_inputs?.body?.[1],
    textByHtmlId(state, `mcss-list_txt-cssm_${elId}`),
    textByClassMatch(state, rootNodeIds, className =>
      className === 'sub_text' ||
      className === 'mcss-sub_text-cssm' ||
      className === 'mcss-list_txt_compact-cssm'
    )
  ) || '';

  // firstNonEmptyString: an empty subject must not win the chain and then
  // collapse the whole title to the text fallback (doc 32 bug: submsges with
  // empty subjects rendered their body text as the blue title link).
  const title = firstNonEmptyString(
    facadeContent.subject,
    legacySub?.subject,
    legacySub?.title,
    legacySub?.namekey_subject,
    legacySub?.name_key,
    legacySub?.code_key,
    textByClassMatch(state, rootNodeIds, className =>
      className.includes('sub_subject') ||
      className.includes('headline_subject') ||
      className === 'mcss-sub_link-cssm'
    )
  ) || text.slice(0, 80) || `Sub ${elId}`;

  const author = firstString(facadeIdentity.display_name, facadeIdentity.name_key, legacySub?.name_key);
  // Legacy compact link rule (subs parser _get_namekey_label/_get_text_label):
  // messages link their NAME KEY, answers their subject. The payload carries
  // it pre-resolved as namekey_subject.
  const namekeySubject = firstNonEmptyString(
    legacySub?.namekey_subject,
    boolFromLegacy(legacySub?.is_msg) ? firstNonEmptyString(legacySub?.name_key, author) : undefined,
  ) || title;

  const html = sanitizeLegacySubSnippetHtml(firstString(
    facadeContent.html,
    legacySub?.html,
    legacySub?.body_html,
    legacySub?.txt_html,
    legacyBodyHtml,
  ));

  return {
    elId,
    title,
    namekeySubject,
    text,
    html,
    datetime: firstDefined(
      facadeTime.formatted,
      facadeTime.create_time,
      legacySub?.datetime,
      legacySub?.create_time,
      legacySub?.created_time,
      legacySub?.timestart_str,
      legacySub?.timestart,
      textByHtmlId(state, `time_${elId}`),
      textByClassMatch(state, rootNodeIds, className => className === 'mcss-list_compact_time-cssm')
    ),
    author,
    avatarPath: firstString(facadeAvatar.path, legacySub?.avatar_path),
    attachments: facadeAttachments,
    access: facade.access,
    permissions: facade.permissions,
    accessGrant: isAccessGrant(facade.access_grant) ? facade.access_grant : undefined,
    email: facade.email,
    display: facade.display,
    // SUB-DIRECT-URL: legacy rows carried a bare relative code key. It only
    // happened to resolve correctly on /parent; from unified folder routes
    // such as /parent/favs, /parent/drafts, and /parent/trash it resolved to
    // the incorrect /parent/codekey. Every row surface consumes this shared
    // state, so pin the row's own code key to one root-relative desk URL.
    href: codeKey ? `/${codeKey.replace(/^\/+/, '')}` : firstString(legacySub?.href, legacySub?.url, legacySub?.link),
    codeKey,
    msgId: firstDefined(legacySub?.msg_id, legacySub?.id),
    subNumber: firstDefined(legacySub?.sub_number, legacySub?.number),
    msgType: firstString(legacySub?.msg_type, legacySub?.msgtype),
    viewType: firstDefined(legacySub?.view_type, legacySub?.viewtype),
    isMsg: boolFromLegacy(legacySub?.is_msg),
    variant: resolveLegacySubVisualVariant(legacySub),
    flags: {
      isVisible: subState.isVisible,
      isChecked: subState.isFlagBlue,
      isFavorite: boolFromLegacy(legacySub?.is_fav) ?? subState.isFav,
      isQuickReplyOpen: subState.isQrOn,
      isHoverHighlighted: subState.isHoverHighlighted,
    },
    counts: {
      // count_subs[0] is the live [answers, submsges] pair the legacy model
      // maintains in place (tree counter writes on insert/removal); the flat
      // count_answers/count_submsges fields are boot-time copies it never
      // touches, so they only serve as fallbacks.
      answers: firstNumber(legacySub?.count_subs?.[0]?.[0], legacySub?.count_answers, legacySub?.answers_count) ?? children.answers.length,
      submsges: firstNumber(legacySub?.count_subs?.[0]?.[1], legacySub?.count_submsges, legacySub?.submsges_count) ?? children.submsges.length,
    },
    children,
    legacy: {
      data: legacySub,
      rootNodeIds,
      rootSlot: rootSlotForSub(state, rootNodeIds),
    },
  };
}
